/**
 * Fantasy Console 0 - Code Editor Component
 *
 * CodeMirror 6 based code editor with JavaScript syntax highlighting,
 * autocomplete, and Fantasy Console API support.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightSpecialChars,
  drawSelection,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  highlightActiveLineGutter,
} from '@codemirror/view'
import { EditorState, Compartment } from '@codemirror/state'
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands'
import { javascript } from '@codemirror/lang-javascript'
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
} from '@codemirror/autocomplete'
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search'
import { lintKeymap } from '@codemirror/lint'
import {
  HighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldGutter,
  foldKeymap,
  defaultHighlightStyle,
} from '@codemirror/language'
import { tags } from '@lezer/highlight'
import { vim, getCM } from '@replit/codemirror-vim'
import type { Extension } from '@codemirror/state'

export type VimMode =
  | 'NORMAL'
  | 'INSERT'
  | 'VISUAL'
  | 'VISUAL LINE'
  | 'VISUAL BLOCK'
import { fantasyConsoleCompletions } from './autocomplete'
import { CodeEditorSettings } from '../../utils/storage'
import { useTheme } from '../../context/ThemeContext'
import type { Theme } from '../../utils/theme'
import styles from './CodeEditor.module.css'

/** Syntax highlight colors per theme ID. Falls back to oneDark-like colors for unknown dark themes. */
const SYNTAX_COLORS: Record<
  string,
  { tag: readonly any[]; color: string; fontStyle?: string }[]
> = {
  dark: [
    { tag: [tags.keyword], color: '#c678dd' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#5c6370',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#98c379' },
    { tag: [tags.number], color: '#d19a66' },
    { tag: [tags.bool], color: '#d19a66' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#61afef',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#e06c75',
    },
    { tag: [tags.typeName], color: '#e5c07b' },
    { tag: [tags.operator], color: '#56b6c2' },
    { tag: [tags.propertyName], color: '#e06c75' },
    { tag: [tags.variableName], color: '#abb2bf' },
    { tag: [tags.regexp], color: '#98c379' },
  ],
  monokai: [
    { tag: [tags.keyword], color: '#f92672' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#75715e',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#e6db74' },
    { tag: [tags.number], color: '#ae81ff' },
    { tag: [tags.bool], color: '#ae81ff' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#a6e22e',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#a6e22e',
    },
    { tag: [tags.typeName], color: '#66d9ef', fontStyle: 'italic' },
    { tag: [tags.operator], color: '#f92672' },
    { tag: [tags.propertyName], color: '#f8f8f2' },
    { tag: [tags.variableName], color: '#f8f8f2' },
    { tag: [tags.regexp], color: '#e6db74' },
  ],
  'solarized-dark': [
    { tag: [tags.keyword], color: '#859900' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#586e75',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#2aa198' },
    { tag: [tags.number], color: '#d33682' },
    { tag: [tags.bool], color: '#cb4b16' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#268bd2',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#268bd2',
    },
    { tag: [tags.typeName], color: '#b58900' },
    { tag: [tags.operator], color: '#859900' },
    { tag: [tags.propertyName], color: '#268bd2' },
    { tag: [tags.variableName], color: '#839496' },
    { tag: [tags.regexp], color: '#2aa198' },
  ],
  nord: [
    { tag: [tags.keyword], color: '#81a1c1' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#616e88',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#a3be8c' },
    { tag: [tags.number], color: '#b48ead' },
    { tag: [tags.bool], color: '#81a1c1' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#88c0d0',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#8fbcbb',
    },
    { tag: [tags.typeName], color: '#ebcb8b' },
    { tag: [tags.operator], color: '#81a1c1' },
    { tag: [tags.propertyName], color: '#88c0d0' },
    { tag: [tags.variableName], color: '#d8dee9' },
    { tag: [tags.regexp], color: '#ebcb8b' },
  ],
  light: [
    { tag: [tags.keyword], color: '#d73a49' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#6a737d',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#032f62' },
    { tag: [tags.number], color: '#005cc5' },
    { tag: [tags.bool], color: '#005cc5' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#6f42c1',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#e36209',
    },
    { tag: [tags.typeName], color: '#22863a' },
    { tag: [tags.operator], color: '#d73a49' },
    { tag: [tags.propertyName], color: '#005cc5' },
    { tag: [tags.variableName], color: '#24292e' },
    { tag: [tags.regexp], color: '#032f62' },
  ],
  warm: [
    { tag: [tags.keyword], color: '#9a4520' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#9a8d7a',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#5a7a3a' },
    { tag: [tags.number], color: '#7a5090' },
    { tag: [tags.bool], color: '#7a5090' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#2a6a8a',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#b06830',
    },
    { tag: [tags.typeName], color: '#5a7a3a' },
    { tag: [tags.operator], color: '#9a4520' },
    { tag: [tags.propertyName], color: '#2a6a8a' },
    { tag: [tags.variableName], color: '#3b3228' },
    { tag: [tags.regexp], color: '#5a7a3a' },
  ],
  dracula: [
    { tag: [tags.keyword], color: '#ff79c6' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#6272a4',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#f1fa8c' },
    { tag: [tags.number], color: '#bd93f9' },
    { tag: [tags.bool], color: '#bd93f9' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#50fa7b',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#50fa7b',
    },
    { tag: [tags.typeName], color: '#8be9fd', fontStyle: 'italic' },
    { tag: [tags.operator], color: '#ff79c6' },
    { tag: [tags.propertyName], color: '#66d9ef' },
    { tag: [tags.variableName], color: '#f8f8f2' },
    { tag: [tags.regexp], color: '#f1fa8c' },
  ],
  ayu: [
    { tag: [tags.keyword], color: '#ff8f40' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#565b66',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#aad94c' },
    { tag: [tags.number], color: '#d2a6ff' },
    { tag: [tags.bool], color: '#d2a6ff' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#ffb454',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#ffb454',
    },
    { tag: [tags.typeName], color: '#39bae6' },
    { tag: [tags.operator], color: '#f29668' },
    { tag: [tags.propertyName], color: '#39bae6' },
    { tag: [tags.variableName], color: '#bfbdb6' },
    { tag: [tags.regexp], color: '#95e6cb' },
  ],
  'tokyo-night': [
    { tag: [tags.keyword], color: '#bb9af7' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#565f89',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#9ece6a' },
    { tag: [tags.number], color: '#ff9e64' },
    { tag: [tags.bool], color: '#ff9e64' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#7aa2f7',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#7dcfff',
    },
    { tag: [tags.typeName], color: '#2ac3de' },
    { tag: [tags.operator], color: '#89ddff' },
    { tag: [tags.propertyName], color: '#7aa2f7' },
    { tag: [tags.variableName], color: '#c0caf5' },
    { tag: [tags.regexp], color: '#b4f9f8' },
  ],
  'rose-pine': [
    { tag: [tags.keyword], color: '#31748f' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#6e6a86',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#f6c177' },
    { tag: [tags.number], color: '#ebbcba' },
    { tag: [tags.bool], color: '#ebbcba' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#9ccfd8',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#e0def4',
    },
    { tag: [tags.typeName], color: '#c4a7e7' },
    { tag: [tags.operator], color: '#908caa' },
    { tag: [tags.propertyName], color: '#9ccfd8' },
    { tag: [tags.variableName], color: '#e0def4' },
    { tag: [tags.regexp], color: '#eb6f92' },
  ],
  matcha: [
    { tag: [tags.keyword], color: '#7a5090' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#8a9a78',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#3a7a5a' },
    { tag: [tags.number], color: '#b06830' },
    { tag: [tags.bool], color: '#b06830' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#3a6a8a',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#5a8a3a',
    },
    { tag: [tags.typeName], color: '#8a5a90' },
    { tag: [tags.operator], color: '#7a5090' },
    { tag: [tags.propertyName], color: '#3a6a8a' },
    { tag: [tags.variableName], color: '#2a3520' },
    { tag: [tags.regexp], color: '#3a7a5a' },
  ],
  sand: [
    { tag: [tags.keyword], color: '#9a4520' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#9a8a70',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#6a8a40' },
    { tag: [tags.number], color: '#8060a0' },
    { tag: [tags.bool], color: '#8060a0' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#2a6080',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#b07830',
    },
    { tag: [tags.typeName], color: '#6a8a40' },
    { tag: [tags.operator], color: '#9a4520' },
    { tag: [tags.propertyName], color: '#2a6080' },
    { tag: [tags.variableName], color: '#3a3020' },
    { tag: [tags.regexp], color: '#6a8a40' },
  ],
  braun: [
    { tag: [tags.keyword], color: '#cc3d20' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#8a8270',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#2060b0' },
    { tag: [tags.number], color: '#d08020' },
    { tag: [tags.bool], color: '#d08020' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#2060b0',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#cc3d20',
    },
    { tag: [tags.typeName], color: '#e07020' },
    { tag: [tags.operator], color: '#cc3d20' },
    { tag: [tags.propertyName], color: '#2060b0' },
    { tag: [tags.variableName], color: '#2c2820' },
    { tag: [tags.regexp], color: '#2060b0' },
  ],
  lavender: [
    { tag: [tags.keyword], color: '#8a4a90' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#8a7aaa',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#3a7a6a' },
    { tag: [tags.number], color: '#b07030' },
    { tag: [tags.bool], color: '#b07030' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#5a6aaa',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#7a5aaa',
    },
    { tag: [tags.typeName], color: '#c06090' },
    { tag: [tags.operator], color: '#8a4a90' },
    { tag: [tags.propertyName], color: '#5a6aaa' },
    { tag: [tags.variableName], color: '#2a2535' },
    { tag: [tags.regexp], color: '#3a7a6a' },
  ],
  'mono-dark': [
    { tag: [tags.keyword], color: '#e0e0e0' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#505050',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#8a8a8a' },
    { tag: [tags.number], color: '#b8b8b8' },
    { tag: [tags.bool], color: '#b8b8b8' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#cccccc',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#e0e0e0',
    },
    { tag: [tags.typeName], color: '#a0a0a0' },
    { tag: [tags.operator], color: '#909090' },
    { tag: [tags.propertyName], color: '#b0b0b0' },
    { tag: [tags.variableName], color: '#c8c8c8' },
    { tag: [tags.regexp], color: '#8a8a8a' },
  ],
  'mono-light': [
    { tag: [tags.keyword], color: '#1a1a1a' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: '#999999',
      fontStyle: 'italic',
    },
    { tag: [tags.string, tags.special(tags.string)], color: '#555555' },
    { tag: [tags.number], color: '#333333' },
    { tag: [tags.bool], color: '#333333' },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: '#2a2a2a',
    },
    {
      tag: [
        tags.definition(tags.variableName),
        tags.definition(tags.propertyName),
      ],
      color: '#1a1a1a',
    },
    { tag: [tags.typeName], color: '#444444' },
    { tag: [tags.operator], color: '#666666' },
    { tag: [tags.propertyName], color: '#3a3a3a' },
    { tag: [tags.variableName], color: '#222222' },
    { tag: [tags.regexp], color: '#555555' },
  ],
}

function buildCMTheme(theme: Theme): Extension {
  const editorTheme = EditorView.theme(
    {
      '&': {
        backgroundColor: theme.colors.bgSecondary,
        color: theme.colors.textPrimary,
      },
      '.cm-content': { caretColor: theme.colors.textPrimary },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: theme.colors.textPrimary,
      },
      '.cm-activeLine': { backgroundColor: theme.colors.overlayTrace },
      '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
        {
          backgroundColor: theme.colors.cmSelectionBg,
        },
      '.cm-gutters': {
        backgroundColor: theme.colors.bgSecondary,
        color: theme.colors.textMuted,
        borderRight: `1px solid ${theme.colors.borderDefault}`,
      },
      '.cm-activeLineGutter': { backgroundColor: theme.colors.bgElevated },
      '.cm-matchingBracket, .cm-nonmatchingBracket': {
        backgroundColor: theme.colors.accentPrimaryAlpha30,
        outline: `1px solid ${theme.colors.accentPrimary}`,
      },
    },
    { dark: theme.isDark },
  )

  const syntaxDefs =
    SYNTAX_COLORS[theme.id] ?? SYNTAX_COLORS[theme.isDark ? 'dark' : 'light']
  const highlightStyle = HighlightStyle.define(syntaxDefs)

  return [editorTheme, syntaxHighlighting(highlightStyle)]
}

export interface EditorPosition {
  cursorPos: number
  scrollTop: number
  scrollLeft: number
}

export interface CodeEditorRef {
  getPosition: () => EditorPosition
}

interface CodeEditorProps {
  value: string
  onChange: (value: string) => void
  onRun?: () => void
  readOnly?: boolean
  settings?: CodeEditorSettings
  onVimModeChange?: (mode: VimMode) => void
  onSavePosition?: (position: EditorPosition) => void
  initialPosition?: EditorPosition
}

const DEFAULT_SETTINGS: CodeEditorSettings = {
  fontSize: 13,
  tabSize: 2,
  lineWrap: false,
  autoRun: false,
  autoRunDebounce: 500,
  vimMode: false,
}

export const CodeEditor = forwardRef<CodeEditorRef, CodeEditorProps>(
  function CodeEditor(
    {
      value,
      onChange,
      onRun,
      readOnly = false,
      settings = DEFAULT_SETTINGS,
      onVimModeChange,
      onSavePosition,
      initialPosition,
    },
    ref,
  ) {
    const { theme } = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const viewRef = useRef<EditorView | null>(null)
    const onChangeRef = useRef(onChange)
    const onRunRef = useRef(onRun)
    const onVimModeChangeRef = useRef(onVimModeChange)
    const onSavePositionRef = useRef(onSavePosition)
    const localValueRef = useRef<string>(value)
    const isExternalUpdateRef = useRef(false)
    const readOnlyCompartment = useRef(new Compartment())
    const fontSizeCompartment = useRef(new Compartment())
    const tabSizeCompartment = useRef(new Compartment())
    const lineWrapCompartment = useRef(new Compartment())
    const vimModeCompartment = useRef(new Compartment())
    const themeCompartment = useRef(new Compartment())

    // Keep refs updated
    onChangeRef.current = onChange
    onRunRef.current = onRun
    onVimModeChangeRef.current = onVimModeChange
    onSavePositionRef.current = onSavePosition

    useImperativeHandle(ref, () => ({
      getPosition: () => {
        const view = viewRef.current
        if (!view) return { cursorPos: 0, scrollTop: 0, scrollLeft: 0 }
        return {
          cursorPos: view.state.selection.main.head,
          scrollTop: view.scrollDOM.scrollTop,
          scrollLeft: view.scrollDOM.scrollLeft,
        }
      },
    }))

    // Save position on unmount (layout effect cleanup runs before regular effect
    // cleanup, so the CodeMirror view is still alive when we capture the position)
    useLayoutEffect(() => {
      return () => {
        if (viewRef.current && onSavePositionRef.current) {
          const view = viewRef.current
          onSavePositionRef.current({
            cursorPos: view.state.selection.main.head,
            scrollTop: view.scrollDOM.scrollTop,
            scrollLeft: view.scrollDOM.scrollLeft,
          })
        }
      }
    }, [])

    // Debounced change handler
    const timeoutRef = useRef<number | null>(null)
    const handleChange = useCallback((newValue: string) => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => {
        localValueRef.current = newValue // Track what we've reported to parent
        onChangeRef.current(newValue)
        timeoutRef.current = null
      }, 150)
    }, [])

    useEffect(() => {
      if (!containerRef.current) return

      const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged && !isExternalUpdateRef.current) {
          const newValue = update.state.doc.toString()
          handleChange(newValue)
        }

        // Check vim mode on any update
        if (settings.vimMode && onVimModeChangeRef.current) {
          const cm = getCM(update.view)
          if (cm?.state?.vim) {
            const vimState = cm.state.vim
            let mode: VimMode = 'NORMAL'
            if (vimState.insertMode) mode = 'INSERT'
            else if (vimState.visualBlock) mode = 'VISUAL BLOCK'
            else if (vimState.visualLine) mode = 'VISUAL LINE'
            else if (vimState.visualMode) mode = 'VISUAL'
            onVimModeChangeRef.current(mode)
          }
        }
      })

      // Custom keymap for run action (Ctrl+Enter on all platforms)
      const runKeymap = keymap.of([
        {
          key: 'Ctrl-Enter',
          run: () => {
            onRunRef.current?.()
            return true
          },
        },
      ])

      const state = EditorState.create({
        doc: value,
        extensions: [
          // Configurable vim mode option
          // Must be before other extensions as per docs
          vimModeCompartment.current.of(settings.vimMode ? vim() : []),

          // Basic setup
          lineNumbers(),
          highlightActiveLineGutter(),
          highlightSpecialChars(),
          history(),
          foldGutter(),
          drawSelection(),
          dropCursor(),
          EditorState.allowMultipleSelections.of(true),
          indentOnInput(),
          syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
          bracketMatching(),
          closeBrackets(),
          autocompletion({
            override: [fantasyConsoleCompletions],
          }),
          rectangularSelection(),
          crosshairCursor(),
          highlightActiveLine(),
          highlightSelectionMatches(),

          // JavaScript language support
          javascript(),

          // Keymaps
          keymap.of([
            ...closeBracketsKeymap,
            ...defaultKeymap,
            ...searchKeymap,
            ...historyKeymap,
            ...foldKeymap,
            ...completionKeymap,
            ...lintKeymap,
            indentWithTab,
          ]),
          runKeymap,

          // Theme (dynamic via compartment)
          themeCompartment.current.of(buildCMTheme(theme)),

          // Configurable options via compartments
          readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
          tabSizeCompartment.current.of(
            EditorState.tabSize.of(settings.tabSize),
          ),
          lineWrapCompartment.current.of(
            settings.lineWrap ? EditorView.lineWrapping : [],
          ),
          fontSizeCompartment.current.of(
            EditorView.theme({
              '&': {
                fontSize: `${settings.fontSize}px`,
              },
            }),
          ),

          // Update listener
          updateListener,

          // Editor styling
          EditorView.theme({
            '&': {
              height: '100%',
              backgroundColor: 'var(--color-bg-primary)',
            },
            '.cm-scroller': {
              fontFamily: 'var(--font-mono)',
              lineHeight: '1.6',
            },
            '.cm-content': {
              padding: '8px 0',
            },
            '.cm-gutters': {
              backgroundColor: 'var(--color-bg-secondary)',
              borderRight: '1px solid var(--color-border-default)',
            },
            '.cm-lineNumbers .cm-gutterElement': {
              padding: '0 8px 0 16px',
            },
          }),
        ],
      })

      const view = new EditorView({
        state,
        parent: containerRef.current,
      })

      viewRef.current = view

      // Restore cursor and scroll position if provided
      if (initialPosition) {
        const cursorPos = Math.min(
          initialPosition.cursorPos,
          view.state.doc.length,
        )
        view.dispatch({ selection: { anchor: cursorPos } })
        // Defer scroll restoration to after layout
        requestAnimationFrame(() => {
          view.scrollDOM.scrollTop = initialPosition.scrollTop
          view.scrollDOM.scrollLeft = initialPosition.scrollLeft
        })
      }

      return () => {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current)
        }
        view.destroy()
        viewRef.current = null
      }
    }, [])

    // Update read-only state when prop changes
    useEffect(() => {
      if (viewRef.current) {
        viewRef.current.dispatch({
          effects: readOnlyCompartment.current.reconfigure(
            EditorState.readOnly.of(readOnly),
          ),
        })
      }
    }, [readOnly])

    // Update settings when they change
    useEffect(() => {
      if (viewRef.current) {
        viewRef.current.dispatch({
          effects: [
            tabSizeCompartment.current.reconfigure(
              EditorState.tabSize.of(settings.tabSize),
            ),
            lineWrapCompartment.current.reconfigure(
              settings.lineWrap ? EditorView.lineWrapping : [],
            ),
            fontSizeCompartment.current.reconfigure(
              EditorView.theme({
                '&': {
                  fontSize: `${settings.fontSize}px`,
                },
              }),
            ),
            vimModeCompartment.current.reconfigure(
              settings.vimMode ? vim() : [],
            ),
          ],
        })
      }
    }, [
      settings.fontSize,
      settings.tabSize,
      settings.lineWrap,
      settings.vimMode,
    ])

    // Update theme when it changes
    useEffect(() => {
      if (viewRef.current) {
        viewRef.current.dispatch({
          effects: themeCompartment.current.reconfigure(buildCMTheme(theme)),
        })
      }
    }, [theme])

    // Update editor content when value prop changes externally
    useEffect(() => {
      const view = viewRef.current
      if (!view) return

      const currentValue = view.state.doc.toString()

      // Only update if value differs AND it's not a change we initiated
      if (value !== currentValue && value !== localValueRef.current) {
        // Clear any pending debounced onChange from the previous file
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }

        // Suppress onChange during programmatic dispatch
        isExternalUpdateRef.current = true
        // Preserve cursor position as best we can
        const cursorPos = Math.min(view.state.selection.main.head, value.length)
        view.dispatch({
          changes: { from: 0, to: currentValue.length, insert: value },
          selection: { anchor: cursorPos },
        })
        isExternalUpdateRef.current = false
      }

      localValueRef.current = value
    }, [value])

    return <div ref={containerRef} className={styles.editor} />
  },
)
